<template>
  <el-container style="height: 100vh;" :style="{ background: mainBgColor }">
    <el-header :style="{ background: adminTheme.headerBgColor, height: '65px', boxShadow: '0 2px 8px rgba(0,21,41,.1)', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1001 }">
      <div class="header-content">
        <h3 class="header-title" :style="{ color: adminTheme.headerTextColor }">资源分享管理系统</h3>
        
        <!-- 模块导航 -->
        <div class="module-nav" ref="moduleNav">
          <div 
            v-for="module in visibleModules" 
            :key="module.id"
            class="module-item"
            :class="{ active: activeModule === module.id }"
            @click="switchModule(module.id)"
            :style="{
              color: activeModule === module.id ? adminTheme.sidebarActiveColor : adminTheme.headerTextColor,
              backgroundColor: activeModule === module.id ? 'rgba(64, 158, 255, 0.1)' : 'transparent'
            }"
          >
            {{ module.name }}
          </div>
          
          <!-- 更多下拉菜单 -->
          <el-dropdown v-if="hiddenModules.length > 0" @command="switchModule" trigger="click">
            <div 
              class="module-item more-item"
              :style="{ color: adminTheme.headerTextColor }">
              更多<i class="el-icon-arrow-down el-icon--right"></i>
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item 
                v-for="module in hiddenModules" 
                :key="module.id"
                :command="module.id"
                :class="{ 'is-active': activeModule === module.id }">
                {{ module.name }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
        
        <div class="header-actions">
          <!-- 任务中心 -->
          <el-popover
            placement="bottom"
            width="500"
            trigger="click"
            v-model="taskPopoverVisible"
            popper-class="task-popover">
            <div class="task-popover-content">
              <div class="popover-header">
                <span class="popover-title">
                  <i class="el-icon-s-order"></i>
                  任务中心
                </span>
                <span class="task-count" v-if="runningTaskCount > 0">正在执行 {{ runningTaskCount }} 个任务</span>
              </div>
              
              <div class="popover-body">
                <div v-if="tasks.length === 0" class="empty-message">
                  <i class="el-icon-info"></i>
                  <p>暂无任务</p>
                </div>
                <div v-else class="task-items">
                  <div 
                    v-for="task in tasks" 
                    :key="task.id" 
                    class="task-item"
                    :class="task.status">
                    <div class="task-icon" :class="task.status">
                      <i :class="getTaskIcon(task.status)"></i>
                    </div>
                    <div class="task-content">
                      <div class="task-title">
                        {{ task.title }}
                        <el-tag v-if="task.status === 'running'" size="mini" type="primary" effect="plain">执行中</el-tag>
                        <el-tag v-else-if="task.status === 'success'" size="mini" type="success" effect="plain">已完成</el-tag>
                        <el-tag v-else-if="task.status === 'failed'" size="mini" type="danger" effect="plain">失败</el-tag>
                      </div>
                      <div class="task-desc">{{ task.description }}</div>
                      <div class="task-progress" v-if="task.status === 'running' && task.progress !== undefined">
                        <el-progress :percentage="task.progress" :status="task.progress === 100 ? 'success' : null"></el-progress>
                      </div>
                      <div class="task-result" v-if="task.status === 'success' && task.result">
                        <span class="result-label">结果：</span>{{ task.result }}
                      </div>
                      <div class="task-error" v-if="task.status === 'failed' && task.error">
                        <i class="el-icon-warning"></i> {{ task.error }}
                      </div>
                      <div class="task-time">{{ formatTaskTime(task.createTime) }}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="popover-footer" v-if="tasks.length > 0">
                <el-button type="text" size="small" @click="clearCompletedTasks">
                  <i class="el-icon-delete"></i> 清除已完成
                </el-button>
              </div>
            </div>
            
            <el-badge :value="runningTaskCount" :max="99" :hidden="runningTaskCount === 0" slot="reference" class="notification-badge">
              <el-button 
                icon="el-icon-s-order" 
                circle 
                class="task-btn"
                :style="{ 
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  color: adminTheme.headerTextColor,
                  width: '50px',
                  height: '50px'
                }">
              </el-button>
            </el-badge>
          </el-popover>
          
          <!-- 消息通知 -->
          <el-popover
            placement="bottom"
            width="400"
            trigger="click"
            v-model="messagePopoverVisible"
            popper-class="message-popover">
            <div class="message-popover-content">
              <div class="popover-header">
                <span class="popover-title">
                  <i class="el-icon-bell"></i>
                  消息通知
                </span>
                <span class="unread-count" v-if="unreadCount > 0">{{ unreadCount }}条未读</span>
              </div>
              
              <div class="popover-body">
                <div v-if="recentMessages.length === 0" class="empty-message">
                  <i class="el-icon-info"></i>
                  <p>暂无新消息</p>
                </div>
                <div v-else class="message-items">
                  <div 
                    v-for="msg in recentMessages" 
                    :key="msg.id" 
                    class="message-item"
                    :class="{ 'unread': msg.isRead === 0 }"
                    @click="handleMessageClick(msg)">
                    <div class="message-icon" :class="msg.type">
                      <i :class="getMessageIcon(msg.type)"></i>
                    </div>
                    <div class="message-content">
                      <div class="message-title">
                        {{ msg.title }}
                        <el-tag v-if="msg.isRead === 0" size="mini" type="danger" effect="dark">未读</el-tag>
                      </div>
                      <div class="message-text">{{ msg.content }}</div>
                      <div class="message-time">{{ formatMessageTime(msg.createTime) }}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="popover-footer" v-if="recentMessages.length > 0">
                <el-button type="text" size="small" @click="markAllAsRead">
                  <i class="el-icon-check"></i> 全部标为已读
                </el-button>
              </div>
            </div>
            
            <el-badge :value="unreadCount" :max="99" :hidden="unreadCount === 0" slot="reference" class="notification-badge">
              <el-button 
                icon="el-icon-bell" 
                circle 
                class="message-btn"
                :style="{ 
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  color: adminTheme.headerTextColor,
                  width: '50px',
                  height: '50px'
                }">
              </el-button>
            </el-badge>
          </el-popover>
          
          <el-dropdown @command="handleCommand">
            <span class="el-dropdown-link">
              <el-avatar v-if="userInfo.avatar" :src="userInfo.avatar" :size="60" style="vertical-align: middle; margin-right: 12px;"></el-avatar>
              <el-avatar v-else icon="el-icon-user-solid" :size="60" style="vertical-align: middle; margin-right: 12px;"></el-avatar>
              <span :style="{ color: adminTheme.headerTextColor, fontSize: '20px', fontWeight: '500', marginRight: '10px' }">{{ userInfo.nickname }}</span>
              <i class="el-icon-arrow-down el-icon--right" :style="{ color: adminTheme.headerTextColor, fontSize: '20px' }"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile">
                <i class="el-icon-user"></i> 个人信息
              </el-dropdown-item>
              <el-dropdown-item command="logout" divided class="logout-item">
                <i class="el-icon-switch-button"></i> 退出登录
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
    </el-header>
    
    <el-container style="margin-top: 65px;">
      <el-aside :width="isCollapse ? '64px' : '200px'" :style="{ background: adminTheme.sidebarBgColor, transition: 'width 0.3s', position: 'fixed', top: '65px', bottom: 0, left: 0, zIndex: 999 }">
        <el-menu
          :default-active="$route.path"
          router
          :collapse="isCollapse"
          :collapse-transition="false"
          :background-color="adminTheme.sidebarBgColor"
          :text-color="adminTheme.sidebarTextColor"
          :active-text-color="adminTheme.sidebarActiveColor"
          style="border-right: none; height: calc(100vh - 105px); overflow-y: auto;">
          <el-menu-item 
            v-for="item in currentMenuItems" 
            :key="item.index"
            :index="item.index" 
            :style="{
              height: '45px',
              lineHeight: '45px',
              fontSize: '15px',
              paddingLeft: isCollapse ? '0 !important' : '20px !important',
              margin: '3px 8px',
              borderRadius: '6px',
              textAlign: isCollapse ? 'center' : 'left',
              transition: 'all 0.3s'
            }">
            <i :class="item.icon" :style="{ fontSize: '18px', marginRight: isCollapse ? '0' : '12px' }"></i>
            <span slot="title">{{ item.title }}</span>
          </el-menu-item>
        </el-menu>
        
        <!-- 收缩按钮（浮动在底部） -->
        <div 
          class="collapse-trigger"
          @click="isCollapse = !isCollapse"
          :style="{ 
            position: 'fixed',
            bottom: '20px',
            left: isCollapse ? '12px' : '0',
            width: isCollapse ? '40px' : '200px',
            height: '40px',
            background: adminTheme.sidebarBgColor,
            color: adminTheme.sidebarTextColor,
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapse ? 'center' : 'flex-start',
            cursor: 'pointer',
            transition: 'all 0.3s',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            paddingLeft: isCollapse ? '0' : '10px'
          }"
        >
          <i :class="isCollapse ? 'el-icon-s-unfold' : 'el-icon-s-fold'" :style="{ fontSize: '16px', marginLeft: isCollapse ? '0' : '0' }"></i>
          <span v-if="!isCollapse" style="margin-left: 8px; font-size: 13px;">收起菜单</span>
        </div>
      </el-aside>
      
      <el-main :style="{ background: mainBgColor, minHeight: 'calc(100vh - 65px)', padding: '20px', marginLeft: isCollapse ? '64px' : '200px', transition: 'margin-left 0.3s' }">
        <router-view :key="$route.fullPath"></router-view>
      </el-main>
    </el-container>
    
    <!-- 消息详情对话框 -->
    <el-dialog
      :visible.sync="messageDetailVisible"
      width="600px"
      :show-close="true"
      class="message-detail-dialog">
      <div slot="title" class="dialog-title">
        <i :class="getMessageIcon(currentMessage?.type)" style="margin-right: 8px;"></i>
        {{ currentMessage?.title }}
      </div>
      
      <div v-if="currentMessage" class="message-detail-content">
        <div class="detail-header">
          <div class="detail-icon" :class="currentMessage.type">
            <i :class="getMessageIcon(currentMessage.type)"></i>
          </div>
          <div class="detail-info">
            <div class="detail-time">
              <i class="el-icon-time"></i>
              {{ formatFullTime(currentMessage.createTime) }}
            </div>
            <el-tag v-if="currentMessage.isRead === 0" size="small" type="danger">未读</el-tag>
            <el-tag v-else size="small" type="info">已读</el-tag>
          </div>
        </div>
        
        <el-divider></el-divider>
        
        <div class="detail-content">
          <p>{{ currentMessage.content }}</p>
        </div>
        
        <div v-if="currentMessage.link" class="detail-link">
          <el-button type="primary" size="small" @click="handleMessageLink(currentMessage.link)">
            <i class="el-icon-link"></i>
            查看相关内容
          </el-button>
        </div>
      </div>
    </el-dialog>
  </el-container>
</template>

<script>
export default {
  name: 'Layout',
  data() {
    // 直接在data中读取localStorage，避免this引用问题
    const getInitialThemeMode = () => {
      const cachedTheme = localStorage.getItem('admin_theme_config')
      if (cachedTheme) {
        try {
          const theme = JSON.parse(cachedTheme)
          return theme.adminThemeMode === 'dark'
        } catch (e) {
          return false
        }
      }
      return false
    }
    
    const getInitialTheme = () => {
      const cachedTheme = localStorage.getItem('admin_theme_config')
      if (cachedTheme) {
        try {
          const theme = JSON.parse(cachedTheme)
          return {
            sidebarBgColor: theme.adminSidebarBgColor || '#304156',
            sidebarTextColor: theme.adminSidebarTextColor || '#bfcbd9',
            sidebarActiveColor: theme.adminSidebarActiveColor || '#409EFF',
            headerBgColor: theme.adminHeaderBgColor || '#ffffff',
            headerTextColor: theme.adminHeaderTextColor || '#303133'
          }
        } catch (e) {
          // 解析失败返回默认值
        }
      }
      return {
        sidebarBgColor: '#304156',
        sidebarTextColor: '#bfcbd9',
        sidebarActiveColor: '#409EFF',
        headerBgColor: '#ffffff',
        headerTextColor: '#303133'
      }
    }
    
    return {
      userInfo: {},
      isDarkMode: getInitialThemeMode(),
      adminTheme: getInitialTheme(),
      activeModule: '', // 默认激活的模块
      modules: [],  // 从数据库加载
      menus: {},     // 从数据库加载
      isCollapse: false, // 菜单是否收缩
      taskPopoverVisible: false, // 任务弹窗是否可见
      tasks: [], // 任务列表
      messagePopoverVisible: false, // 消息弹窗是否可见
      unreadCount: 0, // 未读消息数量
      recentMessages: [], // 最近消息列表
      messageDetailVisible: false, // 消息详情对话框
      currentMessage: null, // 当前查看的消息
      maxVisibleModules: 8 // 最大可见模块数量
    }
  },
  computed: {
    mainBgColor() {
      return this.isDarkMode ? '#1a1a1a' : '#f0f2f5'
    },
    currentMenuItems() {
      // 根据当前激活的模块返回对应的菜单项
      // 注意：menus对象的key是字符串类型，需要转换
      const moduleIdStr = String(this.activeModule)
      return this.menus[moduleIdStr] || []
    },
    runningTaskCount() {
      return this.tasks.filter(t => t.status === 'running').length
    },
    // 可见的模块（前 N 个）
    visibleModules() {
      return this.modules.slice(0, this.maxVisibleModules)
    },
    // 隐藏的模块（放入“更多”下拉菜单）
    hiddenModules() {
      return this.modules.slice(this.maxVisibleModules)
    }
  },
  watch: {
    // 监听路由变化，自动切换模块
    $route(to) {
      this.setActiveModuleByRoute(to.path)
    }
  },
  mounted() {
    const userStr = localStorage.getItem('admin_user')
    if (userStr) {
      this.userInfo = JSON.parse(userStr)
    }
    
    // 同步更新documentElement的class，保持与main.js一致
    this.updateGlobalDarkMode()
    
    this.loadAdminTheme()
    
    // 从后端加载菜单数据
    this.loadMenuData()
    
    // 根据当前路由设置激活模块
    this.setActiveModuleByRoute(this.$route.path)
    
    // 监听主题变化事件
    this.$root.$on('admin-theme-change', (theme) => {
      this.applyTheme(theme)
    })
    
    // 监听用户信息变化事件
    this.$root.$on('user-info-change', (userInfo) => {
      this.userInfo = userInfo
    })
    
    // 加载消息数据
    this.loadUnreadCount()
    this.loadRecentMessages()
    
    // 定时刷新消息，每30秒检查一次
    this.messageTimer = setInterval(() => {
      this.loadUnreadCount()
      this.loadRecentMessages()
    }, 30000)
    
    // 监听窗口大小变化，动态调整模块显示数量
    window.addEventListener('resize', this.calculateMaxVisibleModules)
    this.$nextTick(() => {
      this.calculateMaxVisibleModules()
    })
  },
  beforeDestroy() {
    this.$root.$off('admin-theme-change')
    this.$root.$off('user-info-change')
    
    // 清除消息定时器
    if (this.messageTimer) {
      clearInterval(this.messageTimer)
    }
    
    // 移除窗口大小监听
    window.removeEventListener('resize', this.calculateMaxVisibleModules)
  },
  methods: {
    // 计算最大可见模块数量（根据窗口宽度动态调整）
    calculateMaxVisibleModules() {
      this.$nextTick(() => {
        const windowWidth = window.innerWidth
        
        // 根据窗口宽度动态设置最大可见模块数
        if (windowWidth >= 1920) {
          this.maxVisibleModules = 10
        } else if (windowWidth >= 1600) {
          this.maxVisibleModules = 8
        } else if (windowWidth >= 1400) {
          this.maxVisibleModules = 6
        } else if (windowWidth >= 1200) {
          this.maxVisibleModules = 5
        } else {
          this.maxVisibleModules = 4
        }
      })
    },
    
    // 从后端加载菜单数据
    async loadMenuData() {
      try {
        const res = await this.$axios.get('/admin/permission/menu')
        if (res.data.code === 200 && res.data.data) {
          const menuData = res.data.data
          
          // 设置模块列表
          this.modules = menuData.modules || []
          
          // 检查是否有菜单数据
          if (this.modules.length === 0) {
            this.$message.warning('您的账户还未分配角色或权限，请联系超级管理员分配角色')
            return
          }
          
          // 构建 menus 对象：按模块ID分组
          const menusData = menuData.menus || []
          const menusGrouped = {}
          
          menusData.forEach(menu => {
            if (!menusGrouped[menu.moduleId]) {
              menusGrouped[menu.moduleId] = []
            }
            menusGrouped[menu.moduleId].push({
              index: menu.url,
              title: menu.name,
              icon: menu.icon
            })
          })
          
          this.menus = menusGrouped
          
          // 保存菜单数据到localStorage，供子页面使用
          localStorage.setItem('admin_menu_data', JSON.stringify(menuData))
          
          // 保存权限数据到localStorage
          if (menuData.permissionKeys) {
            localStorage.setItem('admin_permission_data', JSON.stringify({
              permissionKeys: menuData.permissionKeys
            }))
          }
          
          // 设置默认激活模块
          if (this.modules.length > 0 && !this.activeModule) {
            this.activeModule = this.modules[0].id
          }
          
          // 根据当前路由设置激活模块
          this.setActiveModuleByRoute(this.$route.path)
          
          // 如果当前在根路径，自动跳转到第一个有权限的页面
          this.redirectToFirstAvailablePage()
        }
      } catch (error) {
        console.error('加载菜单数据失败:', error)
        this.$message.error('加载菜单失败，请刷新页面')
      }
    },
    updateGlobalDarkMode() {
      // 更新documentElement的class，与main.js保持一致
      if (this.isDarkMode) {
        document.documentElement.classList.add('admin-dark-mode')
      } else {
        document.documentElement.classList.remove('admin-dark-mode')
      }
    },
    async loadAdminTheme() {
      try {
        const res = await this.$axios.get('/admin/config/admin-theme')
        if (res.data.code === 200 && res.data.data) {
          this.applyTheme(res.data.data)
        }
      } catch (error) {
        console.error('加载管理后台主题失败:', error)
        // 即使主题加载失败，也不影响页面正常显示
        // 使用默认主题配置
        const defaultTheme = {
          adminThemeMode: 'light',
          adminSidebarBgColor: '#304156',
          adminSidebarTextColor: '#bfcbd9',
          adminSidebarActiveColor: '#409EFF',
          adminHeaderBgColor: '#ffffff',
          adminHeaderTextColor: '#303133'
        }
        this.applyTheme(defaultTheme)
      }
    },
    applyTheme(config) {
      // 应用主题模式
      this.isDarkMode = config.adminThemeMode === 'dark'
      
      // 更新全局class
      this.updateGlobalDarkMode()
      
      // 应用颜色配置
      this.adminTheme.sidebarBgColor = config.adminSidebarBgColor || '#304156'
      this.adminTheme.sidebarTextColor = config.adminSidebarTextColor || '#bfcbd9'
      this.adminTheme.sidebarActiveColor = config.adminSidebarActiveColor || '#409EFF'
      this.adminTheme.headerBgColor = config.adminHeaderBgColor || '#ffffff'
      this.adminTheme.headerTextColor = config.adminHeaderTextColor || '#303133'
      
      // 缓存到localStorage，避免下次加载闪烁
      localStorage.setItem('admin_theme_config', JSON.stringify(config))
    },
    handleCommand(command) {
      if (command === 'logout') {
        this.$confirm('确认退出登录?', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          localStorage.removeItem('admin_token')
          localStorage.removeItem('admin_user')
          localStorage.removeItem('admin_menu_data')
          // 避免重复导航错误
          if (this.$route.path !== '/login') {
            this.$router.push('/login').catch(err => {
              // 忽略NavigationDuplicated错误，但重新抛出其他错误
              if (err.name !== 'NavigationDuplicated') {
                console.error(err)
              }
            })
          }
        }).catch(() => {
          // 用户点击取消，不做任何操作
        })
      } else if (command === 'profile') {
        // 避免重复导航错误
        if (this.$route.path !== '/profile') {
          this.$router.push('/profile').catch(err => {
            // 忽略NavigationDuplicated错误，但重新抛出其他错误
            if (err.name !== 'NavigationDuplicated') {
              console.error(err)
            }
          })
        }
      }
    },
    // 切换模块
    switchModule(moduleId) {
      this.activeModule = moduleId
      // 自动跳转到该模块的第一个菜单项
      // 注意：menus对象的key是字符串类型，需要转换
      const moduleIdStr = String(moduleId)
      const menuItems = this.menus[moduleIdStr]
      if (menuItems && menuItems.length > 0) {
        const targetPath = menuItems[0].index
        // 避免重复导航到当前路由
        if (this.$route.path !== targetPath) {
          this.$router.push(targetPath).catch(err => {
            // 忽略重复导航错误
            if (err.name !== 'NavigationDuplicated') {
              throw err
            }
          })
        }
      }
    },
    // 根据路由路径设置激活模块
    setActiveModuleByRoute(path) {
      // 遍历所有模块的菜单项，找到匹配的路由
      for (const [moduleId, menuItems] of Object.entries(this.menus)) {
        if (menuItems.some(item => item.index === path)) {
          this.activeModule = moduleId
          return
        }
      }
      
      // 如果没有找到匹配的模块，默认激活第一个模块
      this.activeModule = this.modules.length > 0 ? this.modules[0].id : 'resource'
    },
    // 跳转到第一个有权限的页面
    redirectToFirstAvailablePage() {
      const currentPath = this.$route.path
      
      // 如果已经在具体页面，检查是否有权限
      if (currentPath !== '/' && currentPath !== '') {
        const hasPermission = Object.values(this.menus).some(menuItems =>
          menuItems.some(item => item.index === currentPath)
        )
        
        if (hasPermission) {
          // 当前页面有权限，不需要跳转
          return
        }
      }
      
      // 找到第一个有权限的菜单项
      if (this.modules.length > 0) {
        const firstModuleId = this.modules[0].id
        const firstModuleMenus = this.menus[firstModuleId]
        
        if (firstModuleMenus && firstModuleMenus.length > 0) {
          const targetPath = firstModuleMenus[0].index
          
          // 避免重复导航
          if (currentPath !== targetPath) {
            this.$router.replace(targetPath).catch(err => {
              if (err.name !== 'NavigationDuplicated') {
                console.error('自动跳转失败:', err)
              }
            })
          }
        }
      }
    },
    
    // 加载未读消息数量
    async loadUnreadCount() {
      try {
        const res = await this.$axios.get('/admin/message/unread-count')
        if (res.data.code === 200) {
          this.unreadCount = res.data.data
        }
      } catch (error) {
        console.error('加载未读消息数量失败:', error)
      }
    },
    
    // 加载最近消息列表
    async loadRecentMessages() {
      try {
        const res = await this.$axios.get('/admin/message/recent', {
          params: {
            pageNum: 1,
            pageSize: 5
          }
        })
        if (res.data.code === 200) {
          this.recentMessages = res.data.data.records || []
        }
      } catch (error) {
        console.error('加载最近消息失败:', error)
      }
    },
    
    // 获取消息图标
    getMessageIcon(type) {
      const iconMap = {
        'system': 'el-icon-s-tools',
        'like': 'el-icon-star-on',
        'comment': 'el-icon-chat-dot-round',
        'download': 'el-icon-download'
      }
      return iconMap[type] || 'el-icon-bell'
    },
    
    // 格式化消息时间
    formatMessageTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const now = new Date()
      const diff = now - date
      const minute = 60 * 1000
      const hour = 60 * minute
      const day = 24 * hour
      
      if (diff < minute) {
        return '刚刚'
      } else if (diff < hour) {
        return Math.floor(diff / minute) + '分钟前'
      } else if (diff < day) {
        return Math.floor(diff / hour) + '小时前'
      } else if (diff < 7 * day) {
        return Math.floor(diff / day) + '天前'
      } else {
        return date.toLocaleDateString()
      }
    },
    
    // 点击消息
    async handleMessageClick(msg) {
      // 关闭弹窗
      this.messagePopoverVisible = false
      
      // 标记为已读
      if (msg.isRead === 0) {
        try {
          await this.$axios.put(`/admin/message/${msg.id}/read`)
          this.loadUnreadCount()
          this.loadRecentMessages()
        } catch (error) {
          console.error('标记消息已读失败:', error)
        }
      }
      
      // 显示消息详情
      this.currentMessage = msg
      this.messageDetailVisible = true
    },
    
    // 全部标为已读
    async markAllAsRead() {
      try {
        await this.$axios.put('/admin/message/read-all')
        this.$message.success('已全部标记为已读')
        this.loadUnreadCount()
        this.loadRecentMessages()
        this.messagePopoverVisible = false
      } catch (error) {
        this.$message.error('操作失败')
      }
    },
    
    // 格式化完整时间
    formatFullTime(time) {
      if (!time) return ''
      const date = new Date(time)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    
    // 处理消息链接
    handleMessageLink(link) {
      if (link) {
        this.messageDetailVisible = false
        this.$router.push(link).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })
      }
    },
    
    // ================== 任务中心相关方法 ==================
    // 获取任务图标
    getTaskIcon(status) {
      const icons = {
        running: 'el-icon-loading',
        success: 'el-icon-success',
        failed: 'el-icon-error'
      }
      return icons[status] || 'el-icon-info'
    },
    
    // 格式化任务时间
    formatTaskTime(time) {
      if (!time) return ''
      const now = new Date()
      const taskTime = new Date(time)
      const diff = now - taskTime
      
      // 如果是今天
      if (taskTime.toDateString() === now.toDateString()) {
        return `今天 ${taskTime.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`
      }
      
      // 如果是昨天
      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)
      if (taskTime.toDateString() === yesterday.toDateString()) {
        return `昨天 ${taskTime.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`
      }
      
      // 其他情况显示完整日期
      return taskTime.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    // 清除已完成的任务
    clearCompletedTasks() {
      this.tasks = this.tasks.filter(t => t.status === 'running')
      this.$message.success('已清除已完成任务')
    },
    
    // 添加任务
    addTask(task) {
      this.tasks.unshift(task)
    },
    
    // 更新任务进度
    updateTaskProgress(taskId, progress, result = null) {
      const task = this.tasks.find(t => t.id === taskId)
      if (task) {
        task.progress = progress
        if (result !== null) {
          task.result = result
        }
      }
    },
    
    // 完成任务
    completeTask(taskId, result = null) {
      const task = this.tasks.find(t => t.id === taskId)
      if (task) {
        task.status = 'success'
        task.progress = 100
        if (result !== null) {
          task.result = result
        }
      }
    },
    
    // 任务失败
    failTask(taskId, error) {
      const task = this.tasks.find(t => t.id === taskId)
      if (task) {
        task.status = 'failed'
        task.error = error
      }
    }
  }
}
</script>

<style scoped>
/* 头部布局优化 */
.header-content {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 20px;
  gap: 15px;
}

.header-title {
  font-size: 19px !important;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
  flex-shrink: 0;
}

.module-nav {
  display: flex;
  gap: 8px;
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 5px 10px;
  flex-wrap: nowrap;
  overflow: visible;
}

.module-item {
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: all 0.3s;
  white-space: nowrap;
  flex-shrink: 0;
}

.module-item.more-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.module-item:hover {
  background-color: rgba(64, 158, 255, 0.1);
}

.module-item.active {
  background-color: rgba(64, 158, 255, 0.2);
  font-weight: 600;
}

/* 更多下拉菜单样式 */
>>> .el-dropdown-menu__item.is-active {
  background-color: rgba(64, 158, 255, 0.1);
  color: #409EFF;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

/* 响应式优化 */
@media screen and (max-width: 1600px) {
  .header-title {
    font-size: 17px;
  }
  
  .module-item {
    font-size: 13px;
    padding: 5px 10px;
  }
  
  .module-nav {
    gap: 6px;
  }
}

@media screen and (max-width: 1400px) {
  .header-title {
    font-size: 16px;
  }
  
  .module-item {
    font-size: 12px;
    padding: 4px 8px;
  }
  
  .module-nav {
    gap: 5px;
  }
  
  .header-actions {
    gap: 10px;
  }
}

@media screen and (max-width: 1200px) {
  .header-content {
    padding: 0 15px;
    gap: 10px;
  }
  
  .header-title {
    font-size: 15px;
  }
  
  .module-item {
    font-size: 11px;
    padding: 3px 6px;
  }
}

/* 侧边栏滚动条优化 */
>>> .el-menu {
  height: calc(100vh - 105px) !important;
  overflow-y: auto;
}

>>> .el-menu::-webkit-scrollbar {
  width: 6px;
}

>>> .el-menu::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

>>> .el-menu::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

>>> .el-menu::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* 侧边栏菜单项优化 */
>>> .el-menu-item {
  transition: all 0.3s;
}

>>> .el-menu-item:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

>>> .el-menu-item.is-active {
  background-color: rgba(64, 158, 255, 0.2) !important;
  color: #409EFF !important;
  font-weight: 600;
}

.collapse-trigger {
  z-index: 100;
}

.collapse-trigger:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
  border-color: rgba(255, 255, 255, 0.25) !important;
}

.collapse-trigger:active {
  transform: translateX(-50%) scale(0.95);
}

/* 菜单收缩时的样式优化 */
>>> .el-menu--collapse {
  width: 64px;
}

>>> .el-menu--collapse .el-menu-item {
  padding: 0 !important;
}

>>> .el-menu--collapse .el-menu-item i {
  margin: 0 !important;
}

>>> .el-menu--collapse .el-menu-item span {
  display: none;
}

>>> .el-menu-item {
  transition: all 0.3s;
}

>>> .el-aside {
  overflow: visible;
  height: calc(100vh - 65px) !important;
}

/* 消息通知样式 */
.notification-badge >>> .el-badge__content {
  border: 2px solid #fff;
  font-weight: 600;
}

.message-btn {
  transition: all 0.3s ease;
}

.message-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* 消息弹窗样式 */
>>> .message-popover {
  padding: 0 !important;
}

.message-popover-content {
  max-height: 500px;
  display: flex;
  flex-direction: column;
}

.popover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e4e7ed;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.popover-title {
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.unread-count {
  font-size: 12px;
  background: rgba(255, 255, 255, 0.3);
  padding: 4px 12px;
  border-radius: 12px;
}

.popover-body {
  max-height: 400px;
  overflow-y: auto;
}

.empty-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-message i {
  font-size: 60px;
  color: #dcdfe6;
  margin-bottom: 16px;
}

.empty-message p {
  margin: 0;
  font-size: 14px;
}

.message-items {
  padding: 8px;
}

.message-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 4px;
}

.message-item:hover {
  background: #f5f7fa;
}

.message-item.unread {
  background: #ecf5ff;
}

.message-item.unread:hover {
  background: #d9ecff;
}

.message-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  color: white;
}

.message-icon.system {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.message-icon.like {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.message-icon.comment {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.message-icon.download {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.message-content {
  flex: 1;
  min-width: 0;
}

.message-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-text {
  font-size: 13px;
  color: #606266;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-time {
  font-size: 12px;
  color: #909399;
}

.popover-footer {
  padding: 12px 20px;
  border-top: 1px solid #e4e7ed;
  text-align: center;
  background: #f5f7fa;
}

/* 任务中心样式 */
>>> .task-popover {
  padding: 0 !important;
}

.task-popover-content {
  max-height: 500px;
  display: flex;
  flex-direction: column;
}

.task-popover-content .popover-header {
  background: linear-gradient(135deg, #409EFF 0%, #64b5f6 100%);
}

.task-count {
  font-size: 12px;
  background: rgba(255, 255, 255, 0.3);
  padding: 4px 12px;
  border-radius: 12px;
}

.task-popover-content .popover-body {
  max-height: 400px;
  overflow-y: auto;
}

.task-items {
  padding: 8px;
}

.task-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.3s;
  margin-bottom: 8px;
  border: 1px solid #e4e7ed;
}

.task-item:hover {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.task-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  color: white;
}

.task-icon.running {
  background: linear-gradient(135deg, #409EFF 0%, #64b5f6 100%);
  animation: rotating 2s linear infinite;
}

.task-icon.success {
  background: linear-gradient(135deg, #67c23a 0%, #95de64 100%);
}

.task-icon.failed {
  background: linear-gradient(135deg, #f56c6c 0%, #fab6b6 100%);
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.task-content {
  flex: 1;
  min-width: 0;
}

.task-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.task-desc {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-progress {
  margin-bottom: 8px;
}

.task-result {
  font-size: 13px;
  color: #67c23a;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.result-label {
  font-weight: 600;
}

.task-error {
  font-size: 13px;
  color: #f56c6c;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.task-time {
  font-size: 12px;
  color: #909399;
}

/* 消息详情对话框 */
.message-detail-dialog >>> .el-dialog__header {
  border-bottom: 1px solid #e4e7ed;
  padding: 20px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.message-detail-dialog >>> .el-dialog__title {
  color: white;
}

.message-detail-dialog >>> .el-dialog__headerbtn .el-dialog__close {
  color: white;
  font-size: 20px;
}

.message-detail-dialog >>> .el-dialog__body {
  padding: 24px;
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
}

.message-detail-content {
  min-height: 200px;
}

.detail-header {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.detail-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  color: white;
}

.detail-icon.system {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.detail-icon.like {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.detail-icon.comment {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.detail-icon.download {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.detail-info {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-time {
  font-size: 14px;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 6px;
}

.detail-content {
  padding: 20px 0;
}

.detail-content p {
  font-size: 15px;
  line-height: 1.8;
  color: #303133;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.detail-link {
  padding-top: 16px;
  border-top: 1px solid #e4e7ed;
}
</style>